import { getDistance } from '@nx-smartmonkey/shared/helpers';
import { useMutation } from '@tanstack/react-query';
import * as _ from 'lodash';
import { useEffect, useMemo, useRef } from 'react';

import { capacitorService } from '../../../../application/CapacitorService';
import { routeService } from '../../../../application/RouteService';
import { TrackPositionProps } from '../../../../repositories/route/RouteRepository';
import { LocationActionType } from '../../context/locationContext/actions';
import { useLocationContext } from '../../context/locationContext/context';
import { CurrentPositionType } from '../../context/locationContext/reducer';
import { useRouteContext } from '../../route/context/context';
import { ErrorProps, NewCoordsProps, usePosition } from './usePosition';

const SPEED_THRESHOLD = 50; // 50 m/s or 180 km/h
const ACCURACY_THRESHOLD = 50; // 50 meters
const DISTANCE_THRESHOLD = 10; // 10 meters
const TIME_THRESHOLD = 8000; // 8 seconds

// Hooks act as 'Adapter' layer.
export const useCurrentPosition = () => {
  const { locationState, locationDispatch } = useLocationContext();
  const currentPositionRef = useRef(locationState.currentPosition);
  const source = capacitorService.getPlatform();

  useEffect(() => {
    currentPositionRef.current = locationState.currentPosition;
  }, [locationState.currentPosition]);

  const { routeState } = useRouteContext();

  const { mutate } = useMutation(
    ({ route, lat, lng, source }: TrackPositionProps): Promise<void> =>
      routeService.trackPosition({ route, lat, lng, source }),
    {
      onSuccess: () => {
        console.info(`trackPosition, onSuccess`);
      },
    }
  );

  const debouncedTrackRoute = useMemo(
    () =>
      _.debounce((coordinates: NewCoordsProps) => {
        trackPosition(coordinates, currentPositionRef.current);
      }, 1000),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { setIsTracking } = usePosition({
    onNewCoords: (newCoords: NewCoordsProps) => {
      debouncedTrackRoute(newCoords);
    },
    onError: ({ positionError, customError }: ErrorProps) => {
      console.warn(`Error code:`, positionError?.code);
      console.warn(`Error message:`, positionError?.message || customError);
    },
  });

  useEffect(() => {
    return () => {
      setIsTracking(false);
    };
  }, [setIsTracking]);

  const trackPosition = (
    { latitude: lat, longitude: lng, accuracy }: NewCoordsProps,
    currentPosition?: CurrentPositionType
  ) => {
    const newTimeStamp = new Date().getTime();

    // Only update the current position if the difference between now and last update is greater than the time threshold
    if (newTimeStamp - (currentPosition?.timeStamp ?? 0) < TIME_THRESHOLD) {
      return null;
    }

    const distance = getDistance({ lat: currentPosition?.lat ?? 0, lng: currentPosition?.lng ?? 0 }, { lat, lng });
    const time = (newTimeStamp - (currentPosition?.timeStamp ?? 0)) / 1000;
    const speed = distance / time;

    // Sends track position only if:
    //    - The geo location accuracy is lower than 50 meters
    //    - The difference between last 2 positions is greater that 10 meters
    //    - The speed is lower than 180km/h
    //    - The time difference between last 2 positions is greater than 15 seconds

    if (
      ((accuracy ?? 0) < ACCURACY_THRESHOLD && distance > DISTANCE_THRESHOLD && speed < SPEED_THRESHOLD) ||
      !currentPosition
    ) {
      mutate({ route: routeState.route!, lat, lng, source });
      locationDispatch({
        type: LocationActionType.SET_TRACK_CURRENT_POSITION,
        payload: {
          lat,
          lng,
          timeStamp: newTimeStamp,
        },
      });
    }

    return null;
  };

  return null;
};
